import { paths } from '@/constants/paths'
import { ServerExperiments } from '@/experimentation'
import { runExperimentsOnServer } from '@/experimentation/server'
import { Seo } from '@/layout/Seo'
import { NextPageWithLayout } from '@/layouts/types'
import { getGuildStatus } from '@/services/GuildUserService/GuildUserService'
import { getSweepstakes } from '@/services/SweepstakeService/SweepstakeService'
import { getUserGuildDiscounts } from '@/services/Theatrical/queries'
import { SweepstakeObject, TheatricalReleaseObject } from '@/types/codegen-federation'
import DatadogRumFull from '@/utils/datadog/DatadogRumFull'
import { getServerSidePropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { getBasePath } from '@/utils/sitemap/base'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { SimpleTicketItem } from '@/views/TicketCheckoutViews/hooks/useSimpleTicketList'
import { TicketsLandingView } from '@/views/TicketsLandingView/TicketsLandingView'
import { comingSoonTheatricalReleases } from '@/views/TicketsLandingView/comingSoonTheatrical'
import { TicketsLandingContextProvider } from '@/views/TicketsLandingView/components/TicketsLandingContext'
import { getNewTheatricalLandingReleases, getUserTickets } from '@/views/TicketsLandingView/queries'
import {
  ComingSoonTheatricalRelease,
  CurrentTheatricalRelease,
  PastTheatricalRelease,
} from '@/views/TicketsLandingView/types'
import {
  bySweepstakeEndDateAsc,
  byTheatricalReleaseDateDesc,
  extractCurrentReleases,
  isValidPastTheatricalRelease,
  mapToPastTheatricalRelease,
} from '@/views/TicketsLandingView/utils'

interface TicketsLandingPageProps {
  currentReleases: CurrentTheatricalRelease[]
  comingSoonReleases: ComingSoonTheatricalRelease[]
  pastReleases: PastTheatricalRelease[]
  userTickets: SimpleTicketItem[]
  sweepstakes: SweepstakeObject[]
  isBasicGuildUser: boolean
  isPremiumGuildUser: boolean
  isLoggedIn: boolean
  locale: string
  experiments: ServerExperiments
}

export const TicketsLandingPage: NextPageWithLayout<TicketsLandingPageProps> = ({ locale, experiments, ...props }) => {
  const { t } = useTranslate('tickets')
  const basePath = getBasePath(locale)
  const url = `${basePath}${paths.tickets.index}`
  const description = t(
    'ticketsLandingPageDescriptionV2',
    'Buy tickets and get showtimes for Angel Originals at a theater near you. Explore current, upcoming, and past theatrical releases.',
  )
  const title = t('ticketLandingPageTitleAngel', 'Tickets & Showtimes | In Theaters | Angel')

  const openGraph = {
    url,
    description,
    title,
  }

  return (
    <>
      <DatadogRumFull />
      <Seo canonical={url} description={description} openGraph={openGraph} path={paths.tickets.index} title={title} />
      <TicketsLandingContextProvider experiments={experiments}>
        <TicketsLandingView {...props} />
      </TicketsLandingContextProvider>
    </>
  )
}

export const getServerSideProps = getServerSidePropsErrorHandler<TicketsLandingPageProps>(
  async ({ locale, federationClient, userId, req, res }) => {
    const guildDiscounts = await getUserGuildDiscounts(federationClient, userId)
    const [releases, userTickets, sweepstakes, guildStatus, experiments, translations] = await Promise.all([
      getNewTheatricalLandingReleases({ client: federationClient }),
      getUserTickets({ client: federationClient, userId }),
      getSweepstakes(federationClient, ['LIVE']),
      getGuildStatus(federationClient),
      runExperimentsOnServer(req, res, []),
      loadTranslations(locale, ['common', 'tickets', 'account', 'home']),
    ])

    const isPremiumGuildUser = !!guildStatus?.isGuildMember && !!guildStatus?.guildRoles?.hasFreeTickets
    const isBasicGuildUser = !!guildStatus?.isGuildMember && !guildStatus?.guildRoles?.hasFreeTickets

    const sortedSweepstakes = sweepstakes.sort(bySweepstakeEndDateAsc)

    const currentReleases = extractCurrentReleases({
      releases: releases ?? [],
      sweepstakes: sweepstakes ?? [],
      guildDiscounts: isPremiumGuildUser ? guildDiscounts : [],
    })
    const pastReleases = extractPastReleases(releases ?? [])

    return {
      props: {
        locale,
        experiments,
        currentReleases,
        comingSoonReleases: comingSoonTheatricalReleases,
        pastReleases,
        isBasicGuildUser,
        isPremiumGuildUser,
        isLoggedIn: !!userId,
        userTickets,
        sweepstakes: sortedSweepstakes,
        ...translations,
      },
    }
  },
)

const extractPastReleases = (releases: TheatricalReleaseObject[]): PastTheatricalRelease[] => {
  return releases
    .filter((release) => release?.region?.ticketsStatus === 'ENDED')
    .map(mapToPastTheatricalRelease)
    .filter(isValidPastTheatricalRelease)
    .sort(byTheatricalReleaseDateDesc)
}

export default TicketsLandingPage
